/**
 * LSP-20327 (data-loss, reload boundary): the durable half of the LSP-19767 "this device lost its
 * record of your upload" warning.
 *
 * LSP-19767 surfaces that state as an in-memory tray row. A reload throws that row away, and the
 * row can never be rebuilt from the upload database — being absent from that database is the whole
 * definition of this state. So the person was told once, refreshed, and the warning, its Try again
 * and its Remove all disappeared while the conversation still read "1 Pending".
 *
 * These notices live in `localStorage`, deliberately NOT in `AdminFileUploads`:
 *
 * - The event that creates a notice is that IndexedDB store being wiped. Writing the only copy of
 *   the warning back into the store that just lost the upload puts it one eviction from vanishing
 *   again, in the exact scenario it exists for.
 * - A notice is five short strings. It needs none of IndexedDB's size, and `localStorage` is
 *   synchronous, so restoring on mount cannot lose a race with the first parked-upload pass.
 *
 * A notice is retired when the person acts on the row (Try again that restarts, or Remove that is
 * acknowledged) — see `noteParkedUploadResolvedLocally` in FileUploadDialog.
 */

export interface LostUploadRecordNotice {
	sessionId: string;
	fileName: string;
	fileSize: number;
	fileType: string;
	uploadId?: string;
	conversationId?: string;
	/** Which of the two LSP-19767 sentences the row showed. A restored row must not upgrade doubt to fact. */
	evidence: "absent" | "unverified";
	noticedAt: number;
}

const STORAGE_KEY = "admin.lostUploadRecordNotices.v1";

/** Bounded like the tray's other tombstones so a long-lived browser cannot grow this without end. */
const MAX_NOTICES = 20;

/**
 * ponytail: a fixed expiry, not a per-notice policy. A warning nobody ever acts on would otherwise
 * reopen the tray on every page load forever; a week is long enough to outlive a weekend and short
 * enough that the file is still worth re-uploading. Revisit only if support asks for a longer one.
 */
const NOTICE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function readRaw(): LostUploadRecordNotice[] {
	let stored: string | null = null;
	try {
		stored = globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
	} catch (error) {
		// Silent-failure rule: a private window or a blocked-storage profile is the one place this
		// whole feature cannot work, and it must say so rather than look like "there were no notices".
		console.warn("[Upload] Could not read the lost-upload-record notices; a reload will not restore them", error);
		return [];
	}
	if (!stored) return [];
	try {
		const parsed: unknown = JSON.parse(stored);
		if (!Array.isArray(parsed)) {
			console.warn("[Upload] Discarding lost-upload-record notices that are not a list", { stored: typeof parsed });
			return [];
		}
		const valid = parsed.filter((notice): notice is LostUploadRecordNotice => {
			if (!notice || typeof notice !== "object") return false;
			const candidate = notice as Partial<LostUploadRecordNotice>;
			return (
				typeof candidate.sessionId === "string" &&
				candidate.sessionId.length > 0 &&
				typeof candidate.fileName === "string" &&
				typeof candidate.fileSize === "number" &&
				Number.isFinite(candidate.fileSize) &&
				candidate.fileSize >= 0 &&
				typeof candidate.fileType === "string" &&
				(candidate.evidence === "absent" || candidate.evidence === "unverified") &&
				typeof candidate.noticedAt === "number" &&
				Number.isFinite(candidate.noticedAt) &&
				(candidate.uploadId === undefined || typeof candidate.uploadId === "string") &&
				(candidate.conversationId === undefined || typeof candidate.conversationId === "string")
			);
		});
		if (valid.length !== parsed.length) {
			console.warn("[Upload] Discarding malformed lost-upload-record notices", { dropped: parsed.length - valid.length });
			write(valid);
		}
		return valid;
	} catch (error) {
		console.warn("[Upload] Discarding unreadable lost-upload-record notices", error);
		return [];
	}
}

function write(notices: LostUploadRecordNotice[]): void {
	try {
		globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(notices));
	} catch (error) {
		// Quota or a blocked store. The in-memory row this tab already shows is unaffected; only the
		// reload survival is lost, and that must be visible in the log rather than silently skipped.
		console.warn("[Upload] Could not persist a lost-upload-record notice; it will not survive a reload", {
			count: notices.length,
			error
		});
	}
}

/** Notices still worth showing: not expired. Expired ones are dropped from storage as a side effect. */
export function readLostUploadRecordNotices(now: number = Date.now()): LostUploadRecordNotice[] {
	const stored = readRaw();
	const live = stored.filter((notice) => now - notice.noticedAt < NOTICE_TTL_MS);
	if (live.length !== stored.length) {
		console.warn("[Upload] Dropping lost-upload-record notices nobody acted on within the notice lifetime", {
			dropped: stored.length - live.length
		});
		write(live);
	}
	return live;
}

/**
 * Idempotent by `sessionId`: re-surfacing the same row must not add a second notice, and must not
 * restart its clock either.
 *
 * `noticedAt` is when the person was FIRST told. Every restore on mount re-enters
 * `markLocalUploadRecordLost`, so writing a fresh timestamp here would push the expiry out by the
 * full lifetime on every page load — a warning nobody acts on would reopen the tray forever, which
 * is the exact thing the lifetime exists to stop (Cursor Bugbot, PR #5338).
 */
export function rememberLostUploadRecordNotice(notice: LostUploadRecordNotice): void {
	const held = readRaw();
	const firstNoticedAt = held.find((existing) => existing.sessionId === notice.sessionId)?.noticedAt;
	const others = held.filter((existing) => existing.sessionId !== notice.sessionId);
	const next = [...others, firstNoticedAt === undefined ? notice : { ...notice, noticedAt: firstNoticedAt }];
	write(next.length > MAX_NOTICES ? next.slice(next.length - MAX_NOTICES) : next);
}

export function forgetLostUploadRecordNotice(sessionId: string | undefined): void {
	if (!sessionId) return;
	const held = readRaw();
	const next = held.filter((notice) => notice.sessionId !== sessionId);
	if (next.length === held.length) return;
	write(next);
}
